import { combineLatest, debounceTime, distinctUntilChanged, filter, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { createStore, select, setProp, withProps } from '@ngneat/elf';

import { RouterStoreService } from '@frontend-site/shared/router-store';
import { HybridConfig, HYBRID_CONFIG } from '../entities';
import { relativeElementBounds } from './_positioning-utils';
import { Params } from '@angular/router';

export type IFrameStyle = Record<string, unknown>;

export type IFrameRenderType = 'hidden' | 'onContainer' | 'replaceUi';

interface IframeProperties {
    iframe: HTMLIFrameElement | null;
    container: HTMLElement | null;
    customStyles?: IFrameStyle;
    sourceUrl: SafeResourceUrl | null;
    renderType: IFrameRenderType;
    bounds: DOMRect | null;
}

function createInitialState(): IframeProperties {
    return { iframe: null, renderType: 'hidden', sourceUrl: null, customStyles: {}, container: null, bounds: null };
}

const iframePropertiesStore = createStore({ name: 'hybridIframeProperties' }, withProps<IframeProperties>(createInitialState()));

export const hybridIframe$ = iframePropertiesStore.pipe(select((state) => state.iframe));
export const hybridIframeContainer$ = iframePropertiesStore.pipe(select((state) => state.container));
export const hybridIframeCustomStyles$ = iframePropertiesStore.pipe(select((state) => state.customStyles));
export const hybridIframeSourceUrl$ = iframePropertiesStore.pipe(
    filter((x) => !!x),
    select((state) => state.sourceUrl)
);
export const hybridIframeRenderType$ = iframePropertiesStore.pipe(select((state) => state.renderType));
export const hybridIframeBounds$ = iframePropertiesStore.pipe(select((state) => state.bounds));

function defineIframeRenderType(isOldUrl: boolean, shouldShowInContainer: boolean): IFrameRenderType {
    if (isOldUrl) return 'replaceUi';

    return shouldShowInContainer ? 'onContainer' : 'hidden';
}

@Injectable({
    providedIn: 'root'
})
export class HybridIframeManagerService implements OnDestroy {
    private readonly destroyed$ = new Subject<void>();

    public readonly iframe$ = hybridIframe$;
    public readonly container$ = hybridIframeContainer$;
    public readonly sourceUrl$ = hybridIframeSourceUrl$;
    public readonly customStyles$ = hybridIframeCustomStyles$;
    public readonly renderType$ = hybridIframeRenderType$;
    public readonly bounds$ = hybridIframeBounds$;
    public readonly showNewApp$ = this.renderType$.pipe(
        map((rendering) => rendering !== 'replaceUi'),
        distinctUntilChanged()
    );

    public readonly showOldApp$ = this.renderType$.pipe(
        map((rendering) => rendering !== 'hidden'),
        distinctUntilChanged()
    );

    constructor(
        @Inject(HYBRID_CONFIG) hybridConfig: HybridConfig,
        private readonly sanitizer: DomSanitizer,
        private readonly routerStore: RouterStoreService
    ) {
        this.hookRoutingToRenderTypeSetter();
        this.hookContainerToBoundsSetter();

        this.setSourceUrl(hybridConfig.initialIframeSourceUrl);
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private hookRoutingToRenderTypeSetter(): void {
        const isOldUrl$ = this.routerStore.state$.pipe(
            map((state) => !!state.data?.navState && !state.data?.onContainer),
            distinctUntilChanged()
        );

        const shouldShowInContainer$ = this.container$.pipe(map(Boolean), distinctUntilChanged());

        combineLatest([isOldUrl$, shouldShowInContainer$])
            .pipe(
                takeUntil(this.destroyed$),
                map(([isOldUrl, shouldShowInContainer]) => defineIframeRenderType(isOldUrl, shouldShowInContainer)),
                debounceTime(100)
            )
            .subscribe((renderType) => this.setRenderType(renderType));
    }

    private hookContainerToBoundsSetter(): void {
        this.container$
            .pipe(
                takeUntil(this.destroyed$),
                switchMap((container) => (container ? relativeElementBounds(container) : of(null))),
                debounceTime(100)
            )
            .subscribe((bounds) => this.setBounds(bounds));
    }

    public setIframe(iframe: HTMLIFrameElement): void {
        if (this.getProperty('iframe'))
            throw new Error(`
                Seems like the hybrid iframe has already been initialized.
                Was '<p-hybrid-iframe>' placed twice on the page?
            `);

        iframePropertiesStore.update(setProp('iframe', iframe));
    }

    public clearIframe(): void {
        iframePropertiesStore.update(setProp('iframe', null));
    }

    public setContainer(container: HTMLElement): void {
        iframePropertiesStore.update(setProp('container', container));
    }

    public clearContainer(): void {
        iframePropertiesStore.update(setProp('container', null));
    }

    public setSourceUrl(url: string): void {
        iframePropertiesStore.update(setProp('sourceUrl', this.sanitizer.bypassSecurityTrustResourceUrl(url)));
    }

    public setCustomStyle(style: IFrameStyle): void {
        iframePropertiesStore.update(setProp('customStyles', style));
    }

    public clearCustomStyle(): void {
        iframePropertiesStore.update(setProp('customStyles', undefined));
    }

    public getProperty<TKey extends keyof IframeProperties>(name: TKey): IframeProperties[TKey] {
        return iframePropertiesStore.getValue()[name];
    }

    private setRenderType(renderType: IFrameRenderType): void {
        iframePropertiesStore.update(setProp('renderType', renderType));
    }

    private setBounds(bounds: DOMRect | null): void {
        iframePropertiesStore.update(setProp('bounds', bounds));
    }
}
